import { createAppResourceService } from "@/global";
import { useNotify } from "@imminently/react/store/notifications";
import { createContext, PropsWithChildren, useContext, useEffect, useRef } from "react";
import { WORK_OFFLINE } from "@/resources/constants";
import { mockSettingsResponse } from "./mock/mockSettings";
import { useAuth } from "react-oidc-context";

/**
 * A simple settings manager to allow sub pages to access and update app settings.
 * Currently only supports a single settings object with id 'trier'. (this effectively the tenancy)
 * Right now we are keeping it simple and just storing the settings in a single object.
 * Updating the settings requires updating the entire object.
 *
 * We don't have a need for complex settings management yet, so keeping it very minimal.
 */

const settingsService = createAppResourceService("meta/settings");

const useAppSettings = () => {
  const notify = useNotify();
  const auth = useAuth();
  /**
   * One-shot redirect guard to prevent login loops.
   * Ensures we only trigger signinRedirect() once per mount when unauthenticated or on 401.
   */
  const redirectingRef = useRef(false);

  // Use mock data when offline, otherwise use the real service
  const liveService = settingsService.useGetOne("trier", {
    refetchOnWindowFocus: false,
    retry: false,
    enabled: !WORK_OFFLINE && auth.isAuthenticated && !auth.isLoading
  });
  const { mutateAsync: updateSettings, ...setQuery } = settingsService.useUpdate();

  // Mock implementation for offline mode
  const mockData = WORK_OFFLINE ? mockSettingsResponse : null;
  const mockQuery = {
    isLoading: false,
    isFetching: false,
    isFetched: true,
    isError: false,
    error: null,
    refetch: () => Promise.resolve(),
  };

  const data = WORK_OFFLINE ? mockData : liveService.data;
  const getQuery = WORK_OFFLINE ? mockQuery : liveService;

  console.log("settings", data);
  console.log("auth state", {
    isAuthenticated: auth.isAuthenticated,
    isLoading: auth.isLoading,
    hasUser: !!auth.user,
    userExpired: auth.user?.expired
  });

  // If not authenticated and not loading, redirect to sign-in immediately
  useEffect(() => {
    if (WORK_OFFLINE) return;
    if (!auth.isLoading && (!auth.user || !auth.isAuthenticated)) {
      if (redirectingRef.current || auth.activeNavigator) return;
      console.warn("[Settings] No auth token present. Redirecting to sign-in.");
      redirectingRef.current = true;
      auth.signinRedirect();
    }
  }, [auth.isLoading, auth.user, auth.isAuthenticated, auth]);

  // If the settings request fails due to auth (401), initiate sign-in
  useEffect(() => {
    if (WORK_OFFLINE) return;
    if (liveService.isError && liveService.error) {
      const message = (liveService.error as Error).message ?? String(liveService.error);
      if (message.includes("401") || message.includes("Unauthorized") || message.includes("token is not found")) {
        console.warn("[Settings] 401 detected fetching meta/settings. Redirecting to sign-in.");
        if (!redirectingRef.current && !auth.activeNavigator) {
          redirectingRef.current = true;
          auth.signinRedirect();
        }
      }
    }
  }, [liveService.isError, liveService.error, auth]);

  /**
   * Update the settings by merging the new settings with the existing ones.
   * @param newSettings Partial settings object to merge with existing settings.
   */
  const setSettings = async (newSettings: any) => {
    if (!data) return;

    if (WORK_OFFLINE) {
      // In offline mode, just log the update and show success
      console.log("Mock settings update:", { ...data, ...newSettings });
      notify.success("Settings updated successfully! (Offline mode)");
      return;
    }

    try {
      const res = await updateSettings({ id: "trier", payload: { ...data, ...newSettings } });
      notify.success("Settings updated successfully!");
      console.log("Updated settings:", res);
    } catch (err) {
      console.error("Failed to update settings:", err);
      notify.error("Failed to update settings. Please try again.");
    }
  };

  return {
    settings: data as any,
    isLoading: getQuery.isLoading || setQuery.isLoading,
    setSettings,
  };
};

const SettingsContext = createContext<ReturnType<typeof useAppSettings> | null>(null);

export const useSettingsContext = () => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error("useSettingsContext must be used within a SettingsProvider");
  }
  return context;
};

export const SettingsProvider = ({ children }: PropsWithChildren) => {
  const settings = useAppSettings();

  return (
    <SettingsContext.Provider value={settings}>
      {children}
    </SettingsContext.Provider>
  );
};